import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useTranslator } from 'tradera-lang/translate';
import SimpleModal from 'components/modals/simple-modal';
import { BasicCarousel } from 'tradera-components/carousels/basic';
import { BREAKPOINT_SIZES, BREAKPOINTS } from 'tradera-constants/breakpoints';
import CloseIcon from '@tradera/blueprint/theme/icons2/close.svg?jsx';
import './image-gallery-modal.scss';

const ImageGalleryModal = props => {
    const { images, initialSlideIndex, closeModalCallback, ...rest } = props;

    const { t } = useTranslator();
    const carouselRef = useRef(null);
    const closeButtonRef = useRef(null);

    const environment = useSelector(state => state.environment);

    const { isMobileDevice } = environment;

    const handleCloseModalClick = event => {
        event.stopPropagation();
        closeModalCallback();
    };

    const stopClickEventPropagationFromCarousel = event =>
        event.stopPropagation();

    const stopClickEventsFromImage = event => event.stopPropagation();

    const navigate = useCallback(e => {
        if (e.keyCode === 37) {
            // left
            carouselRef.current.previous();
            e.preventDefault();
        } else if (e.keyCode === 39) {
            // right
            carouselRef.current.next();
            e.preventDefault();
        }
    }, []);

    useEffect(() => {
        closeButtonRef.current.focus();
        window.addEventListener('keydown', navigate);
        return () => {
            window.removeEventListener('keydown', navigate);
        };
    }, [navigate]);

    const settings = {
        initialSlide: initialSlideIndex,
        adaptiveHeight: true,
        lazyLoad: 'progressive',
        fade: !isMobileDevice,
        useCss: false,
        useTransform: false,
        responsive: [
            {
                breakpoint: BREAKPOINT_SIZES[BREAKPOINTS.MD].min,
                settings: {
                    arrows: false
                }
            }
        ]
    };

    return (
        <SimpleModal
            className={
                'image-gallery-modal d-flex justify-content-center align-items-center w-100 h-100'
            }
            classNameBody={'w-100'}
            closeModalCallback={closeModalCallback}
            {...rest}>
            <div onClick={stopClickEventPropagationFromCarousel}>
                <BasicCarousel
                    ref={carouselRef}
                    className="w-100"
                    settings={settings}
                    render={() =>
                        images.map((image, index) => (
                            <div
                                key={index}
                                className={'image-gallery-modal__image-gutter'}
                                onClick={handleCloseModalClick}>
                                <div
                                    className="position-relative"
                                    onClick={stopClickEventsFromImage}>
                                    <img
                                        src={
                                            image.normal ||
                                            '//static.tradera.net/internal/site/auction/noImage_344x258.gif'
                                        }
                                        className="mx-auto d-block mw-100"
                                        alt={image.alt}
                                    />
                                </div>
                            </div>
                        ))
                    }
                />
                <button
                    title={t('vip_image_gallery_modal_close')}
                    ref={closeButtonRef}
                    className="image-gallery-close btn btn-lg btn-translucent btn-round"
                    onClick={handleCloseModalClick}>
                    <CloseIcon className="icon icon-md" />
                </button>
            </div>
        </SimpleModal>
    );
};

ImageGalleryModal.propTypes = {
    images: PropTypes.array.isRequired,
    initialSlideIndex: PropTypes.number
};
ImageGalleryModal.defaultProps = {
    initialSlideIndex: 0
};

export { ImageGalleryModal };
